/**
 * Client-side SHA-256 hashing for ECL PII.
 *
 * All plaintext customer PII (email, phone, names) is hashed in the browser
 * before it ever crosses Flouri's wire. The backend only sees hashes, so
 * plaintext PII never reaches Cloud Logging, request traces, or our database.
 * This shrinks the GDPR/CCPA blast radius around CSV-imported customer lists.
 *
 * Output is lowercase hex (Data Manager API `encoding: "HEX"`). Use this for
 * email/phone/first_name/last_name; country_code and postal_code stay in
 * plaintext per the Data Manager spec.
 */

/** SHA-256, output as lowercase hex. */
export async function sha256Hex(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const buffer = encoder.encode(input);
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Normalize an email per Google's ECL rules (lowercase + trim) THEN hash.
 * Gmail's dotted-alias normalization is intentionally not applied — Google
 * Ads documents that the hash should be of the user-entered form, lowercased.
 */
export async function hashEmail(email: string): Promise<string> {
  const normalized = email.trim().toLowerCase();
  return sha256Hex(normalized);
}

/**
 * Normalize phone to E.164 (+CountryCodeNumber) then hash. Strips spaces,
 * dashes, parens, and prepends `+` if the cleaned digits don't already start
 * with one. We deliberately do NOT infer a country: if the digits already
 * look like an E.164 number (6–15 digits, optional leading `+`), we hash
 * them as-is; anything else throws. Misidentifying a country code is worse
 * than getting no match, so the caller's responsibility is to provide a
 * complete international number — we just tolerate omission of the `+`.
 */
export async function hashPhone(phone: string): Promise<string> {
  const cleaned = phone.replace(/[\s\-().]/g, '');
  // Reject obviously invalid input rather than hash a nonsense string.
  if (!/^\+?\d{6,15}$/.test(cleaned)) {
    throw new Error(
      'Phone must be E.164 (e.g. "+14155551234") with country code'
    );
  }
  const e164 = cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
  return sha256Hex(e164);
}

/** Per ECL spec: lowercase + trim + hash. Names with accents stay as-is. */
export async function hashName(name: string): Promise<string> {
  return sha256Hex(name.trim().toLowerCase());
}
